import { Box, Text } from 'grommet';
import React from 'react';
import FontAwesomeIcon from '../../FontAwesomeIcon';
import {
    faSync as IconSync,
} from '@fortawesome/pro-light-svg-icons';
import { formatLastSynced } from '../../../utils/dates';
import Button from '../../Button';
import Menu from '../../Menu';

const SyncIcon = ({isSyncing}) => (
    <Box animation={isSyncing ? { type: "rotateRight", duration: 1350 } : undefined}>
        <FontAwesomeIcon color='text-weak' SelectedIcon={IconSync} size="xsmall" />
    </Box>
);

const SyncButton = (props) => {
    const {
        isShopifyLinked,
        isNetSuiteLinked,
        isHubSpotLinked,
        triggerSyncNetSuite,
        isNetSuiteSyncing,
        triggerSyncShopify,
        isShopifySyncing,
        triggerSyncHubSpot,
        isHubSpotSyncing,
    } = props;

    const linkedCount = [isShopifyLinked, isNetSuiteLinked, isHubSpotLinked].filter(Boolean).length;

    if (linkedCount === 0) return <></>;

    if (linkedCount === 1) {
        const config = isShopifyLinked
            ? { trigger: triggerSyncShopify, syncing: isShopifySyncing, name: 'Shopify', event: 'sync_shopify' }
            : isNetSuiteLinked
                ? { trigger: triggerSyncNetSuite, syncing: isNetSuiteSyncing, name: 'NetSuite', event: 'sync_netsuite' }
                : { trigger: triggerSyncHubSpot, syncing: isHubSpotSyncing, name: 'HubSpot', event: 'sync_hubspot' };

        return (
            <Button
                event={{section: 'sync_button', element: config.event}}
                secondary
                onClick={config.trigger}
                disabled={config.syncing}
                icon={<SyncIcon isSyncing={config.syncing} />}
                label={config.syncing ? "Sync in progress" : `Sync ${config.name}`}
            />
        );
    }

    return (
        <Box round="xxsmall" border={{color: 'border', size: '1.5px'}} background="foreground" overflow="hidden">
            <Menu
                event={{section: 'sync_button_menu', element: 'menu'}}
                {...getSyncMenuButtonProps(props)}
            />
        </Box>
    );
}

const LastSyncedText = ({syncing, lastSynced}) => {
    if (syncing) return <Text size="small" color="text-weak" weight="normal">Syncing...</Text>;
    const text = formatLastSynced(lastSynced);
    if (!text) return null;
    return <Text size="small" color="text-weak" weight="normal">{text}</Text>;
};

export const getSyncMenuButtonProps = ({
    triggerSyncNetSuite,
    isNetSuiteSyncing,
    triggerSyncShopify,
    isShopifySyncing,
    triggerSyncHubSpot,
    isHubSpotSyncing,
    isShopifyLinked,
    isNetSuiteLinked,
    isHubSpotLinked,
    shopifySyncData,
    netsuiteSyncData,
    hubspotSyncData,
    isMobile
}) => {
    const anySyncing = isNetSuiteSyncing || isShopifySyncing || isHubSpotSyncing;
    const items = [];

    if (isShopifyLinked) {
        items.push({
            label: (
                <Box>
                    <Text weight="normal">Shopify</Text>
                    <LastSyncedText syncing={isShopifySyncing} lastSynced={shopifySyncData?.[0]?.last_synced} />
                </Box>
            ),
            onClick: triggerSyncShopify,
            disabled: isShopifySyncing,
            event: {section: 'sync_button_menu', element: 'sync_shopify'}
        });
    }

    if (isNetSuiteLinked) {
        items.push({
            label: (
                <Box>
                    <Text weight="normal">NetSuite</Text>
                    <LastSyncedText syncing={isNetSuiteSyncing} lastSynced={netsuiteSyncData?.last_synced} />
                </Box>
            ),
            onClick: triggerSyncNetSuite,
            disabled: isNetSuiteSyncing,
            event: {section: 'sync_button_menu', element: 'sync_netsuite'}
        });
    }

    if (isHubSpotLinked) {
        items.push({
            label: (
                <Box>
                    <Text weight="normal">HubSpot</Text>
                    <LastSyncedText syncing={isHubSpotSyncing} lastSynced={hubspotSyncData?.last_sync_at} />
                </Box>
            ),
            onClick: triggerSyncHubSpot,
            disabled: isHubSpotSyncing,
            event: {section: 'sync_button_menu', element: 'sync_hubspot'}
        });
    }

    return {
        icon: false,
        label: (
            <Box direction="row" gap="small" align="center">
                <SyncIcon isSyncing={anySyncing} />
                {anySyncing ?
                    (isMobile ? "Syncing..." : "Sync in progress") :
                    "Sync"}
            </Box>
        ),
        dropProps: {
            border: true,
            align: { top: 'bottom', right: 'right' },
            margin: { vertical: 'xsmall', horizontal: 'xsmall' },
        },
        items
    };
};

export default SyncButton;
